import { InjectionToken, type Signal } from '@angular/core';

export interface IInfiniteScrollResult<T> {
  items: T[];
  totalElements: number;
}

export type InfiniteScrollLoader<TQuery, T> = (
  query: TQuery,
  pageIndex: number,
  pageSize: number,
  abortSignal: AbortSignal,
) => Promise<IInfiniteScrollResult<T>>;

export interface IInfiniteScrollCollectionVm<T> {
  items: T[];
  totalElements: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | undefined;
  hasMore: boolean;
  hasLoadedMore: boolean;
  isStale: boolean;
  lastLoadedAt: Date | null;
  staleSince: Date | null;
}

export interface IInfiniteScrollCollectionOptions<TQuery, T> {
  query: Signal<TQuery | null>;
  pageSize: number;
  loadPage: InfiniteScrollLoader<TQuery, T>;
  getItemKey?: (item: T) => string;
  loadingDelayMs?: number;
  /**
   * Keeps the already loaded data when the query turns null (collection idle)
   * instead of clearing it. Useful for collections whose activation depends on
   * external reactive conditions that may temporarily turn off.
   */
  keepDataOnIdle?: boolean;
}

export interface IInfiniteScrollCollection<T> {
  readonly vm: Signal<IInfiniteScrollCollectionVm<T>>;

  loadNextPage(): void;

  refresh(source: InfiniteScrollRefreshSource): void;

  retry(): void;
}

export type InfiniteScrollRefreshSource = 'polling' | 'user';

export const INFINITE_SCROLL_LOADING_DELAY_MS = new InjectionToken<number>('INFINITE_SCROLL_LOADING_DELAY_MS', {
  factory: () => 200,
});
